import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const POInvoiceHeader = ({
  invoiceNumber,
  onBack,
  onHistory,
  onAssignTo,
  onStart,
  onStop,
  onReject,
  approverRoles = [],
  loadingRoles = false,
  assigningRole = false,
  loadingAction = null, // 'start', 'stop', 'reject', or null
  invoiceStatus = '', // Invoice status to determine if we show Forward to button
}) => {
  const [forwardMenuAnchor, setForwardMenuAnchor] = useState(null);
  const [buttonWidth, setButtonWidth] = useState(null);

  // Show Forward to button for pending and pending_rejection (admin should be able to reassign)
  const statusLower = (invoiceStatus || '').toLowerCase().trim();
  const isPending = statusLower === 'pending' || statusLower === 'pending_rejection';
  const isPendingRejection = statusLower === 'pending_rejection';
  const isStopped = statusLower === 'stop';
  const isActionLoading = loadingAction !== null;

  const handleForwardClick = (event) => {
    const buttonElement = event.currentTarget;
    setButtonWidth(buttonElement.offsetWidth);
    setForwardMenuAnchor(buttonElement);
  };

  const handleForwardClose = () => {
    setForwardMenuAnchor(null);
  };

  const handleAssignToRole = (role) => {
    if (assigningRole) {
      return; // Prevent multiple clicks during API call
    }
    if (onAssignTo) {
      onAssignTo(role);
    }
    // Keep menu open during API call to show loading state
    // Menu will close after API completes via navigation
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #E8E9E9',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onBack}
          disableRipple
          sx={{
            backgroundColor: '#FFF',
            width: 40,
            height: 40,
            borderRadius: '8px',
            boxShadow: '0 0 30px 2px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ArrowBackIosIcon sx={{ ml: '2px', fontSize: '16px' }} />
        </IconButton>
        <Typography
          sx={{
            fontSize: '24px',
            fontFamily: 'Inter',
            fontWeight: 700,
            color: 'black',
          }}
        >
          Invoice {invoiceNumber}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onHistory}
          sx={{
            borderColor: '#E8E9E9',
            color: '#192126',
            textTransform: 'none',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              borderColor: '#BD1D23',
              backgroundColor: 'rgba(189, 29, 35, 0.04)',
            },
          }}
        >
          Invoice Tracking
        </Button>

        {/* Start/Stop buttons - Show Start when stopped, Show Stop when pending */}
        {isStopped && onStart ? (
          <Button
            variant="outlined"
            onClick={onStart}
            disabled={isActionLoading || assigningRole}
            startIcon={
              loadingAction === 'start' ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              borderColor: '#10B981',
              color: '#10B981',
              textTransform: 'none',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#10B981',
                backgroundColor: 'rgba(189, 29, 35, 0.04)',
              },
              '&:disabled': {
                borderColor: '#E8E9E9',
                color: '#9CA3AF',
              },
            }}
          >
            Start
          </Button>
        ) : isPending && onStop ? (
          <Button
            variant="outlined"
            onClick={onStop}
            disabled={isActionLoading || assigningRole}
            startIcon={
              loadingAction === 'stop' ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              borderColor: '#BD1D23',
              color: '#BD1D23', // Red color for Stop
              textTransform: 'none',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#BD1D23',
                backgroundColor: 'rgba(189, 29, 35, 0.04)',
              },
              '&:disabled': {
                borderColor: '#E8E9E9',
                color: '#9CA3AF',
              },
            }}
          >
            Stop
          </Button>
        ) : null}

        {/* Reject button - only for pending_rejection */}
        {isPendingRejection && onReject ? (
          <Button
            variant="outlined"
            onClick={onReject}
            disabled={isActionLoading || assigningRole}
            startIcon={
              loadingAction === 'reject' ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{
              borderColor: '#BD1D23',
              color: '#BD1D23',
              textTransform: 'none',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#BD1D23',
                backgroundColor: 'rgba(189, 29, 35, 0.04)',
              },
              '&:disabled': {
                borderColor: '#E8E9E9',
                color: '#9CA3AF',
              },
            }}
          >
            Reject
          </Button>
        ) : null}

        {isPending && onAssignTo && (
          <>
            <Button
              variant="outlined"
              endIcon={<ArrowDropDownIcon />}
              onClick={handleForwardClick}
              sx={{
                borderColor: '#E8E9E9',
                color: '#192126',
                textTransform: 'none',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#BD1D23',
                  backgroundColor: 'rgba(189, 29, 35, 0.04)',
                },
              }}
            >
              Forward to
            </Button>
            <Menu
              anchorEl={forwardMenuAnchor}
              open={Boolean(forwardMenuAnchor)}
              onClose={handleForwardClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: {
                  minWidth: buttonWidth ? `${buttonWidth}px` : '200px',
                  mt: 0.5,
                  maxHeight: '400px',
                  '& .MuiMenuItem-root': {
                    minHeight: '36px',
                    padding: '8px 16px',
                    '&:not(:last-child)': {
                      borderBottom: '1px solid #E8E9E9',
                    },
                  },
                },
              }}
            >
              {loadingRoles ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading roles...
                    </Typography>
                  </Box>
                </MenuItem>
              ) : approverRoles.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    No roles available
                  </Typography>
                </MenuItem>
              ) : assigningRole ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Assigning invoice...
                    </Typography>
                  </Box>
                </MenuItem>
              ) : (
                approverRoles.map((role) => (
                  <MenuItem key={role.id} onClick={() => handleAssignToRole(role)}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 400,
                        color: '#192126',
                      }}
                    >
                      {role.invoice_type ? `${role.name} (${role.invoice_type})` : role.name}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
};

export default POInvoiceHeader;
